/**
 * useOverlays
 * ───────────
 * Every overlay's visibility + the contextual data each overlay needs.
 *
 * Overlays managed:
 *   - Project detail
 *   - Challenge detail
 *   - Create project (new / fork / multifork / challenge-response / edit)
 *   - Create challenge
 *   - Curate overlay
 *   - Messaging drawer
 *   - Legend popover
 *
 * Place at: src/hooks/useOverlays.js
 */

import { useState, useCallback } from 'react';

export default function useOverlays() {
  // ── Project detail ─────────────────────────────────────────────────────
  const [showProjectDetail, setShowProjectDetail] = useState(false);
  const [projectCuratedBranches, setProjectCuratedBranches] = useState([]);

  // ── Challenge detail ───────────────────────────────────────────────────
  const [showChallengeDetail, setShowChallengeDetail] = useState(false);

  // ── Create challenge ───────────────────────────────────────────────────
  const [showCreateChallenge, setShowCreateChallenge] = useState(false);
  const [challengeProjectContext, setChallengeProjectContext] = useState(null);
  const [editChallengeData, setEditChallengeData] = useState(null); // null = create, object = edit

  // ── Create / edit project ──────────────────────────────────────────────
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [createMode, setCreateMode] = useState('new'); // 'new' | 'fork' | 'multifork' | 'challenge-response' | 'edit'
  const [forkParents, setForkParents] = useState([]);
  const [responseChallenge, setResponseChallenge] = useState(null);
  const [editProject, setEditProject] = useState(null);

  // ── Curate overlay ─────────────────────────────────────────────────────
  const [showCurateOverlay, setShowCurateOverlay] = useState(false);

  // ── Messaging drawer ───────────────────────────────────────────────────
  const [showMessaging, setShowMessaging] = useState(false);

  // ── Legend ──────────────────────────────────────────────────────────────
  const [showLegend, setShowLegend] = useState(false);

  // ── Getting started dismissal ──────────────────────────────────────────
  const [dismissedGettingStarted, setDismissedGettingStarted] = useState(false);

  // ── Convenience openers ────────────────────────────────────────────────

  const openProjectDetail = useCallback(() => {
    setShowProjectDetail(true);
  }, []);

  const closeProjectDetail = useCallback(() => {
    setShowProjectDetail(false);
    setProjectCuratedBranches([]);
  }, []);

  const openChallengeDetail = useCallback(() => {
    setShowChallengeDetail(true);
  }, []);

  const closeChallengeDetail = useCallback(() => {
    setShowChallengeDetail(false);
  }, []);

  const openCreateProject = useCallback(({ mode = 'new', parents = [], challenge = null, project = null } = {}) => {
    setCreateMode(mode);
    setForkParents(parents);
    setResponseChallenge(challenge);
    setEditProject(project);
    setShowCreateProject(true);
  }, []);

  const closeCreateProject = useCallback(() => {
    setShowCreateProject(false);
    setForkParents([]);
    setResponseChallenge(null);
    setEditProject(null);
    setCreateMode('new');
  }, []);

  const openCreateChallenge = useCallback((project) => {
    setChallengeProjectContext(project);
    setEditChallengeData(null);
    setShowCreateChallenge(true);
  }, []);

  const openEditChallenge = useCallback((challenge, project) => {
    setChallengeProjectContext(project || null);
    setEditChallengeData(challenge);
    setShowCreateChallenge(true);
  }, []);

  const closeCreateChallenge = useCallback(() => {
    setShowCreateChallenge(false);
    setChallengeProjectContext(null);
    setEditChallengeData(null);
  }, []);

  const openCurate = useCallback(() => {
    setShowCurateOverlay(true);
  }, []);

  const closeCurate = useCallback(() => {
    setShowCurateOverlay(false);
  }, []);

  const toggleMessaging = useCallback((open) => {
    setShowMessaging(typeof open === 'boolean' ? open : prev => !prev);
  }, []);

  const toggleLegend = useCallback(() => {
    setShowLegend(s => !s);
  }, []);

  // ── Public API ─────────────────────────────────────────────────────────

  return {
    // Project detail
    showProjectDetail,
    projectCuratedBranches,
    setProjectCuratedBranches,
    openProjectDetail,
    closeProjectDetail,

    // Challenge detail
    showChallengeDetail,
    openChallengeDetail,
    closeChallengeDetail,

    // Create project
    showCreateProject,
    createMode,
    forkParents,
    responseChallenge,
    editProject,
    openCreateProject,
    closeCreateProject,

    // Create challenge
    showCreateChallenge,
    challengeProjectContext,
    editChallengeData,
    openCreateChallenge,
    openEditChallenge,
    closeCreateChallenge,

    // Curate
    showCurateOverlay,
    openCurate,
    closeCurate,

    // Messaging
    showMessaging,
    toggleMessaging,

    // Legend
    showLegend,
    toggleLegend,
    setShowLegend,

    // Getting started
    dismissedGettingStarted,
    setDismissedGettingStarted,
  };
}
